<script setup lang="ts">
import BasicInput from '~/components/form-elements/BasicInput.vue'
import BasicInputDecorator from '~/components/form-elements/BasicInputDecorator.vue'
import Checkbox from '~/components/form-elements/Checkbox.vue'
import { debounce } from '~/helpers/debounce-helper'
import { useBrandsStore } from '~/store/brands'
import { useSearchboosterStore } from '~/store/searchbooster'
import type { BrandData } from '~/types/brands'

const brandsStore = useBrandsStore()
const { getBrands, isDataLoading: isBrandsLoading } = storeToRefs(brandsStore)
const searchboosterStore = useSearchboosterStore()

const debouncedBrands = debounce(async (signal: AbortSignal) => {
  brandsStore.clearBrands()
  await brandsStore.fetchBrands(signal)
}, 300)

const vendors = computed({
  get: () => searchboosterStore.filter.params.vendors,
  set: (value) => {
    searchboosterStore.filter.params.vendors = value
  }
})

async function initFetchMoreBrands (): Promise<void> {
  brandsStore.page.number++

  await brandsStore.fetchBrands()
}

async function onBrandChecked (event: any, brand: BrandData): Promise<void> {
  const { checked } = event.target

  if (checked) {
    searchboosterStore.addFilterByVendor(brand.id, brand.attributes.name)
  } else {
    searchboosterStore.deleteFilterByVendorId(brand.id)
  }

  await searchboosterStore.initSearch(true)
}

watch(
  () => brandsStore.filter.query,
  () => {
    brandsStore.page.number = 1
    debouncedBrands()
  }
)

onMounted (async (): Promise<void> => {
  brandsStore.page.size = 5

  await brandsStore.fetchBrands()
})
</script>

<template>
  <div class="flex h-[calc(100vh-180px-max(15vh,72px))] flex-col md:h-full">
    <BasicInputDecorator class="md:mb-2">
      <BasicInput
        v-model="brandsStore.filter.query"
        :is-border="false"
        placeholder="Поиск по бренду"
      />
    </BasicInputDecorator>

    <div class="custom-scroll flex flex-1 flex-col items-center overflow-y-auto md:items-start">
      <div class="w-full">
        <Checkbox
          v-for="(x, index) in getBrands"
          :key="x.id"
          v-model="vendors"
          class="
            mb-0 border-b-[0.5px] border-[#E2E2E2] py-3 !text-sm text-main-dark
            last:border-none
            md:mb-3 md:border-none md:py-0 md:!text-base
          "
          :input-value="x.id"
          is-new-design
          :label-text="x.attributes.name"
          :name="x.id + '-' + index"
          @on-checkbox-changed="(event: any) => onBrandChecked(event, x)"
        />
      </div>

      <LoadMoreLink
        v-if="(brandsStore.page.number + 1) * brandsStore.page.size < brandsStore.totalBrands"
        class="p-3"
        :is-loading="isBrandsLoading"
        @click.prevent="initFetchMoreBrands"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'assets/css/custom-scroll';
</style>
